import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Spinner } from "reactstrap";
import {
  IoClose,
  IoPrintOutline,
  IoCreateOutline,
  IoShareSocialOutline,
  IoCopyOutline,
  IoTrashOutline,
  IoCheckmarkDoneCircleOutline,
  IoCubeOutline,
  IoDocumentTextOutline
} from "react-icons/io5";
import { get, post } from "../../../../../utils/helpers/api";
import { toast } from "react-toastify";
import DefaultButton from "../../../../../components/Buttons/Default";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import DOC_STYLES from "../../../utils/docStyles";
import { AWS_S3_URL, BASE_URL } from "../../../../../utils/constants";
import ShareConfirmationModal from "../../Quotes/Modals/ShareConfirmation";
import { useTranslation } from "react-i18next";
import LegalIdentifierLines from "../../../../../components/Documents/LegalIdentifierLines";
import { amountInWords, amountInWordsPrefix } from "../../../../../utils/helpers/numberToWords";
import { useSettings } from "../../../../../context/settings";
import { printSlug } from "../../../../../utils/helpers/text";
import DefaultDrawer from "../../../../../components/Drawers/Default";

const PRIMARY = "#6C63FF";

function fmt(n) {
  return parseFloat(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ViewDrawer({ deliveryNoteId, isOpen, onClose, onDelete, refreshDeliveryNotes }) {
  const { t, i18n } = useTranslation();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [deliveryNote, setDeliveryNote] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [shareConfirmationModalOpen, setShareConfirmationModalOpen] = useState(false);

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const getDeliveryNote = async () => {
    if (!deliveryNoteId) return;

    try {
      const data = await get(`/sales/delivery-notes/view/${deliveryNoteId}`);
      setDeliveryNote(data.delivery_note ?? null);
      setCompanyData(data.company ?? null);
    } catch (error) {
      toast.error(t('sales.failedLoadDeliveryNote'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!deliveryNoteId) return;
    setLoading(true);
    setDeliveryNote(null);
    getDeliveryNote();
  }, [deliveryNoteId]);

  const currency = (deliveryNote?.currency ?? "usd").toUpperCase();
  const showPrices = !!deliveryNote?.show_prices;

  const customerName = deliveryNote?.customer
    ? (deliveryNote.customer.company_name || `${deliveryNote.customer.firstname ?? ""} ${deliveryNote.customer.lastname ?? ""}`.trim())
    : "—";

  const salespersonName = deliveryNote?.sales_person
    ? `${deliveryNote.sales_person.firstname ?? ""} ${deliveryNote.sales_person.lastname ?? ""}`.trim()
    : null;

  const handlePrint = () => window.print();
  const handleEdit = () => { onClose(); navigate(`/sales/delivery-notes/edit/${deliveryNote.id}`, { state: { deliveryNoteData: { ...deliveryNote } } }); };

  const markAsSent = async () => {
    const data = await post(`/sales/delivery-notes/mark-as-sent/${deliveryNote.id}`);
    if (data?.success) {
      getDeliveryNote();
      refreshDeliveryNotes();
      toast.success(t('sales.deliveryNoteMarkedSent'));
    } else {
      toast.error(data?.message || t('sales.failedSentDeliveryNote'));
    }
  };

  const markAsDelivered = async () => {
    const data = await post(`/sales/delivery-notes/mark-as-delivered/${deliveryNote.id}`);
    if (data?.success) {
      getDeliveryNote();
      refreshDeliveryNotes();
      toast.success(t('sales.deliveryNoteMarkedDelivered'));
    } else {
      toast.error(data?.message || t('sales.failedDeliveredDeliveryNote'));
    }
  };

  const convertToInvoice = async () => {
    const data = await post(`/sales/delivery-notes/convert/${deliveryNote.id}`);
    if (data?.success) {
      toast.success(t('sales.deliveryNoteConverted'));
      navigate(`/sales/invoices/edit/${data.invoice.id}`, { state: { invoiceData: { ...data.invoice } } });
    } else {
      toast.error(data?.message || t('sales.failedConvertDeliveryNote'));
    }
  };

  const clone = async () => {
    const data = await post(`/sales/delivery-notes/clone/${deliveryNote.id}`);
    if (data?.success) {
      toast.success(t('sales.deliveryNoteCloned'));
      navigate(`/sales/delivery-notes/edit/${data.delivery_note.id}`, { state: { deliveryNoteData: { ...data.delivery_note } } });
    } else {
      toast.error(data?.message || t('sales.failedCloneDeliveryNote'));
    }
  };

  const handleShare = async () => {
    const data = await post(`/sales/delivery-notes/share/${deliveryNote.id}`);
    if (data?.success) {
      toast.success(t('sales.deliveryNoteSharedCopied'));
      navigator.clipboard.writeText(BASE_URL + '/delivery-note/' + deliveryNote?.id);
      refreshDeliveryNotes();
    } else {
      toast.error(data?.message || t('sales.failedShareDeliveryNote'));
    }
  }

  const deliveryNoteLabel = deliveryNote ? `${t('sales.deliveryNoteHash')}${deliveryNote.delivery_note_number || deliveryNote.id}` : t('sales.deliveryNoteDetails');

  const deliveryAddressLines = deliveryNote?.delivery_address
    ? deliveryNote.delivery_address.split("\n")
    : null;

  return (
    <DefaultDrawer
      isOpen={isOpen}
      size={680}
      toggle={onClose}
    >
      {loading ?
        <Spinner color="primary" className="d-block mx-auto my-4" />
      :
        <div style={DOC_STYLES.wrap}>
          {/* ── Header ── */}
          <div style={DOC_STYLES.header}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                style={{ background: "none", border: "none", cursor: "pointer", color: "#888", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" }}
                onClick={onClose}
                onMouseEnter={e => { e.currentTarget.style.background = "#f0f0f0"; e.currentTarget.style.color = "#333"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#888"; }}
              >
                <IoClose size={20} />
              </button>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#1d1d1f" }}>{deliveryNoteLabel}</span>
              {deliveryNote &&
                <Badge color="primary">{ deliveryNote.status }</Badge>
              }
            </div>
          </div>

          {/* ── Action Bar ── */}
          <div style={DOC_STYLES.actionBar}>
            <DefaultButton className="btn-action" size="sm" onClick={handleEdit} disabled={!deliveryNote || deliveryNote.stock_applied} color="light" outline>
              <IoCreateOutline size={15} />
              {t('common.edit')}
            </DefaultButton>
            <DefaultButton className="btn-action" size="sm" onClick={() => setShareConfirmationModalOpen(true)} disabled={!deliveryNote} color="light" outline>
              <IoShareSocialOutline size={15} />
              {t('common.share')}
            </DefaultButton>
            <DefaultButton className="btn-action" size="sm" onClick={handlePrint} disabled={!deliveryNote} color="light" outline>
              <IoPrintOutline size={15} />
              {t('common.download')}
            </DefaultButton>
            {!deliveryNote?.stock_applied &&
              <DefaultButton className="btn-action" size="sm" onClick={markAsDelivered} disabled={!deliveryNote} color="light" outline>
                <IoCubeOutline size={15} />
                {t('sales.markAsDelivered')}
              </DefaultButton>
            }
            <Dropdown isOpen={dropdownOpen} toggle={toggle} direction="down" size="sm">
              <DropdownToggle outline color="light" className="btn-action">
                {t('common.options')} <PiDotsThreeOutlineVerticalFill />
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem onClick={markAsSent} disabled={!deliveryNote || deliveryNote.status === "sent"}>
                  <IoCheckmarkDoneCircleOutline size={16} /> {t('common.sent')}
                </DropdownItem>
                <DropdownItem onClick={convertToInvoice} disabled={!deliveryNote || !!deliveryNote.invoice_id}>
                  <IoDocumentTextOutline size={16} /> {t('sales.convertToInvoice')}
                </DropdownItem>
                <DropdownItem onClick={clone} disabled={!deliveryNote}>
                  <IoCopyOutline size={16} /> {t('sales.clone')}
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem className="text-danger" onClick={onDelete} disabled={!deliveryNote}>
                  <IoTrashOutline size={16} /> {t('common.delete')}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* ── Scrollable document area ── */}
          <div style={DOC_STYLES.scroll}>
            {!loading && deliveryNote && (
              <div id="quote-print-area" style={DOC_STYLES.paper}>

                {/* Company + Delivery Note Title */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 36 }}>
                  <div>
                    <img
                      src={companyData?.logo_path ? AWS_S3_URL + companyData.logo_path : require('../../../../../assets/images/brand.svg').default}
                      height={38}
                      style={{ marginBottom: 10 }}
                      alt={t('common.logo')}
                    />
                    <div style={{ fontWeight: 800, fontSize: 18, color: "#1d1d1f", lineHeight: 1.2 }}>
                      { companyData?.legal_name || t('common.companyNamePlaceholder') }
                    </div>
                    <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                      { companyData?.street_address || t('sales.addressPlaceholder') }<br />
                      { companyData?.city || t('common.city') } { companyData?.state || t('common.state') } { companyData?.postal_code || t('common.postalCode') }<br />
                      { printSlug(companyData?.country) || t('common.country') }<br />
                      { companyData?.email || t('common.emailPlaceholder') }
                    </div>
                    <LegalIdentifierLines party={companyData} compact />
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 800, fontSize: 26, color: PRIMARY, letterSpacing: 2, lineHeight: 1.1, textTransform: "uppercase" }}>
                      {t('sales.deliveryNote')}
                    </div>
                    <div style={{ color: "#888", fontSize: 14, marginTop: 6, fontWeight: 600 }}>
                      #{deliveryNote.delivery_note_number || deliveryNote.id}
                    </div>
                  </div>
                </div>

                <hr style={DOC_STYLES.divider} />

                {/* Deliver To + Meta */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
                  <div>
                    <div style={DOC_STYLES.label}>{t('sales.deliverTo')}</div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#1d1d1f" }}>{customerName}</div>
                    {deliveryAddressLines ? (
                      <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                        {deliveryAddressLines.map((line, i) => (
                          <span key={i}>{line}<br /></span>
                        ))}
                      </div>
                    ) : deliveryNote?.customer?.addresses?.length > 0 && (
                      <div style={{ fontSize: 12, color: "#777", marginTop: 6, lineHeight: 1.8 }}>
                        { deliveryNote.customer.addresses[0].street }<br />
                        { deliveryNote.customer.addresses[0].city }, { deliveryNote.customer.addresses[0].state } { deliveryNote.customer.addresses[0].postal_code }<br />
                        { printSlug(deliveryNote.customer.addresses[0].country) }
                      </div>
                    )}
                    <LegalIdentifierLines party={deliveryNote?.customer} compact />
                  </div>
                  <div>
                    <table style={DOC_STYLES.metaTable}>
                      <tbody>
                        <tr>
                          <td style={DOC_STYLES.metaThTd}>{t('sales.deliveryDate')}</td>
                          <td style={DOC_STYLES.metaTd}>{formatDate(deliveryNote.delivery_date) || "—"}</td>
                        </tr>
                        {salespersonName && (
                          <tr>
                            <td style={DOC_STYLES.metaThTd}>{t('sales.salesperson')}</td>
                            <td style={DOC_STYLES.metaTd}>{salespersonName}</td>
                          </tr>
                        )}
                        {deliveryNote.driver_name && (
                          <tr>
                            <td style={DOC_STYLES.metaThTd}>{t('sales.driverName')}</td>
                            <td style={DOC_STYLES.metaTd}>{deliveryNote.driver_name}</td>
                          </tr>
                        )}
                        {deliveryNote.vehicle_registration && (
                          <tr>
                            <td style={DOC_STYLES.metaThTd}>{t('sales.vehicleRegistration')}</td>
                            <td style={DOC_STYLES.metaTd}>{deliveryNote.vehicle_registration}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {deliveryNote.subject && (
                  <div style={{ marginBottom: 24, fontSize: 13, background: "#f9f9ff", border: "1px solid #ede9ff", borderRadius: 7, padding: "10px 14px", color: "#444" }}>
                    <strong style={{ color: PRIMARY }}>{t('sales.re')}</strong>&nbsp;{deliveryNote.subject}
                  </div>
                )}

                {/* Items table */}
                <table style={DOC_STYLES.itemsTable}>
                  <thead>
                    <tr>
                      <th style={{ ...DOC_STYLES.th, width: 36, borderRadius: "6px 0 0 0" }}>#</th>
                      <th style={DOC_STYLES.th}>{t('common.itemDescription')}</th>
                      <th style={{ ...DOC_STYLES.thRight, width: 60, ...(showPrices ? {} : { borderRadius: "0 6px 0 0" }) }}>{t('common.qty')}</th>
                      {showPrices && (
                        <>
                          <th style={{ ...DOC_STYLES.thRight, width: 100 }}>{t('common.unitPrice')}</th>
                          <th style={{ ...DOC_STYLES.thRight, width: 100 }}>{t('sales.discount')}</th>
                          <th style={{ ...DOC_STYLES.thRight, width: 100, borderRadius: "0 6px 0 0" }}>{t('common.total')}</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {deliveryNote.items.length === 0 ? (
                      <tr>
                        <td colSpan={showPrices ? 6 : 3} style={{ ...DOC_STYLES.td, textAlign: "center", color: "#aaa", padding: "32px 0" }}>{t('common.noItemsAdded')}</td>
                      </tr>
                    ) : (
                      deliveryNote.items.map((item, i) => (
                        <tr key={item.id ?? i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                          <td style={DOC_STYLES.td}>{i + 1}</td>
                          <td style={DOC_STYLES.td}>
                            <span style={{ fontWeight: 600 }}>{item.product_name || "—"}</span>
                            {item.description && (
                              <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>{item.description}</div>
                            )}
                          </td>
                          <td style={DOC_STYLES.tdRight}>{item.quantity}</td>
                          {showPrices && (
                            <>
                              <td style={DOC_STYLES.tdRight}>{fmt(item.unit_price)}</td>
                              <td style={DOC_STYLES.tdRight}>{fmt(item.discount_amount)}</td>
                              <td style={DOC_STYLES.tdRight}>{fmt(item.total_price)}</td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {showPrices && (
                  <>
                    {/* Totals */}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                      <div style={DOC_STYLES.totalsBox}>
                        <div style={DOC_STYLES.totalsLine}>
                          <span>{t('common.subTotal')}</span>
                          <span style={{ fontWeight: 500 }}>{fmt(deliveryNote?.sub_total ?? 0)} {currency}</span>
                        </div>
                        {deliveryNote.discount_amount > 0 && (
                          <div style={DOC_STYLES.totalsLine}>
                            <span>{t('sales.discount')}</span>
                            <span style={{ fontWeight: 500, color: "red" }}>- {fmt(deliveryNote?.discount_amount ?? 0)} {currency}</span>
                          </div>
                        )}
                        {deliveryNote.tax_amount > 0 && (
                          <div style={DOC_STYLES.totalsLine}>
                            <span>{t('sales.taxWithRate', { rate: deliveryNote.tax_rate ?? 0 })}</span>
                            <span style={{ fontWeight: 500 }}>{fmt(deliveryNote?.tax_amount ?? 0)} {currency}</span>
                          </div>
                        )}
                        <div style={DOC_STYLES.totalsBold}>
                          <span>{t('common.total')}</span>
                          <span style={{ color: PRIMARY }}>{fmt(deliveryNote?.total_amount ?? 0)} {currency}</span>
                        </div>
                      </div>
                    </div>

                    {/* Amount in words */}
                    <div style={{ marginTop: 20, paddingTop: 14, borderTop: "1px dashed #ddd", fontSize: 12.5, color: "#444" }}>
                      <span style={{ color: "#888" }}>{amountInWordsPrefix(i18n.language)} </span>
                      <span style={{ fontWeight: 600, textTransform: "capitalize" }}>
                        {amountInWords(deliveryNote?.total_amount ?? 0, i18n.language, deliveryNote?.currency)}
                      </span>
                    </div>
                  </>
                )}

                {/* Notes */}
                {deliveryNote.notes && (
                  <div style={{ marginTop: 36, paddingTop: 20, borderTop: "1px solid #eee" }}>
                    <div style={DOC_STYLES.label}>{t('sales.notes')}</div>
                    <p style={{ fontSize: 13, color: "#555", whiteSpace: "pre-wrap", margin: 0, lineHeight: 1.7 }}>
                      {deliveryNote.notes}
                    </p>
                  </div>
                )}

                {/* Signature blocks — a delivery note is signed on receipt */}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 40, marginTop: 48 }}>
                  <div style={{ flex: 1 }}>
                    <div style={DOC_STYLES.label}>{t('sales.deliveredBy')}</div>
                    <div style={{ borderBottom: "1px solid #ccc", height: 56 }} />
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>{t('sales.dateAndSignature')}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={DOC_STYLES.label}>{t('sales.receivedBy')}</div>
                    <div style={{ borderBottom: "1px solid #ccc", height: 56 }} />
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>{t('sales.dateAndSignature')}</div>
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      }

      <ShareConfirmationModal
        isOpen={shareConfirmationModalOpen}
        toggle={() => setShareConfirmationModalOpen(!shareConfirmationModalOpen)}
        onConfirm={() => handleShare()}
      />
    </DefaultDrawer>
  );
}
